import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Threads the draw-sequence run a canvas item's subtree owns and its canvas's
 * rank. Each item accumulates `z_final` itself. A range, not a number, lets a
 * y-sort pass re-pack its run with no view of the rest (`canvasPaintOrder.ts`).
 */
import { createContext, useContext, useMemo } from 'react';
import { allocateNodePaintRange, canvasRenderOrder, layerRankOf, layerRanks, WHOLE_CANVAS_RANGE, } from '../canvasPaintOrder';
import { useCanvasLayerIndex } from '../lighting2d/canvasItemPlacement';
const PaintRangeContext = createContext(WHOLE_CANVAS_RANGE);
PaintRangeContext.displayName = 'PaintRangeContext';
/**
 * The draw-sequence run this node and its subtree own. The default is the
 * whole canvas, which a node outside a 2D stage gets.
 */
export function usePaintRange() {
    return useContext(PaintRangeContext);
}
export function PaintRangeProvider({ value, children }) {
    return _jsx(PaintRangeContext.Provider, { value: value, children: children });
}
const NO_CANVAS_ROOTS = new Map();
const CanvasRootRangesContext = createContext(NO_CANVAS_ROOTS);
CanvasRootRangesContext.displayName = 'CanvasRootRangesContext';
/**
 * Where each canvas root draws (`canvasRootRanges`). A canvas item whose parent
 * is not one draws at its pre-order rank among the roots, not at its nesting
 * slot, so a parent looks its children up here first.
 */
export function useCanvasRootRanges() {
    return useContext(CanvasRootRangesContext);
}
export function CanvasRootRangesProvider({ value, children, }) {
    return _jsx(CanvasRootRangesContext.Provider, { value: value, children: children });
}
const LayerRankContext = createContext(layerRanks([]));
LayerRankContext.displayName = 'LayerRankContext';
/** The rank of `layer` among the canvas layers this scene declares. */
export function useLayerRank(layer) {
    return layerRankOf(useContext(LayerRankContext), layer);
}
export function LayerRanksProvider({ value, children }) {
    return _jsx(LayerRankContext.Provider, { value: value, children: children });
}
/**
 * The `renderOrder` of this canvas item's group, Godot's whole draw-order key.
 * A slice outside `<CanvasItem2D>` calls it too, so the order has one source.
 * The caller passes `effectiveZ`, already accumulated for the light cull.
 */
export function useCanvasItemRenderOrder(node, effectiveZ) {
    const range = usePaintRange();
    const layerRank = useLayerRank(useCanvasLayerIndex());
    const sequence = useMemo(() => allocateNodePaintRange(range, node).self, [range, node]);
    return canvasRenderOrder({ layerRank, zFinal: effectiveZ, sequence });
}
//# sourceMappingURL=PaintOrderContext.js.map